import React, { useEffect, useState } from 'react';
import { Form, FormFeedback, Input, FormGroup, Label } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import Switch from "react-switch";
import { Offsymbol, OnSymbol } from '../../../constants/holcim';
import { BaseCustomModal, GradientDecorationLine, BaseCustomButton, BaseSelectComponent } from '../../../shared';
import { useFormik } from "formik";
import * as Yup from "yup";

import {
    getLabTypes,
    getLabAreas,
    getUsers
} from '../../../store/actions';

export const DefaultAssignmentRegisterModal = ({ setShowModal, showModal, modalRespInfo, isUpdate, rowInfo, modalClose }) => {
    const dispatch = useDispatch();
    const [active, setActive] = useState(false);
    const [lockActive, setLockActive] = useState(false);

    // Fetch catalogs from redux
    const labTypes = useSelector(state => state.labType.labtypes) || [];
    const labAreas = useSelector(state => state.labAreas.labareas) || [];
    const users = useSelector(state => state.usersReducer.users) || [];

    useEffect(() => {
        if (showModal) {
            dispatch(getLabTypes('?limit=1000&page=0'));
            dispatch(getLabAreas('?limit=1000&page=0'));
            dispatch(getUsers('?limit=2000&page=0&recordStatus=enable'));

            if (isUpdate) {
                formikFormData.setValues({
                    assignmentType: rowInfo?.assignmentType || '',
                    laboratoryType: rowInfo?.laboratoryType?.id || '',
                    laboratoryArea: rowInfo?.laboratoryArea?.id || '',
                    user: rowInfo?.user?.id || '',
                });
                setActive(rowInfo?.recordStatus || false);
                setLockActive(false);
            } else {
                formikFormData.resetForm();
                setActive(true);
                setLockActive(true);
            }
        }
    }, [showModal, isUpdate, rowInfo]);

    const formikFormData = useFormik({
        enableReinitialize: true,
        initialValues: {
            assignmentType: isUpdate ? rowInfo?.assignmentType || '' : '',
            laboratoryType: isUpdate ? rowInfo?.laboratoryType?.id || '' : '',
            laboratoryArea: isUpdate ? rowInfo?.laboratoryArea?.id || '' : '',
            user: isUpdate ? rowInfo?.user?.id || '' : '',
        },
        validationSchema: Yup.object({
            assignmentType: Yup.string().required("Seleccione el tipo de proceso"),
            laboratoryType: Yup.string().required("Seleccione el tipo de laboratorio"),
            user: Yup.string().required("Seleccione el usuario responsable"),
            laboratoryArea: Yup.string().when('assignmentType', {
                is: 'Pruebas de Laboratorio',
                then: Yup.string().required("Seleccione el área de laboratorio"),
                otherwise: Yup.string().nullable()
            })
        }),
        onSubmit: (values) => {
            const submitValues = {
                assignmentType: values.assignmentType,
                laboratoryType: values.laboratoryType,
                user: values.user,
            };
            if (values.assignmentType === 'Pruebas de Laboratorio' && values.laboratoryArea) {
                submitValues.laboratoryArea = values.laboratoryArea;
            }
            modalRespInfo(submitValues, active);
            formikFormData.resetForm();
            setShowModal(false);
        }
    });

    const onClosed = () => {
        formikFormData.resetForm();
        modalClose();
        setShowModal(false);
    };

    // Filter users based on selected assignment type
    const filteredUsers = React.useMemo(() => {
        if (formikFormData.values.assignmentType === 'Validación de informes') {
            return users.filter(u => u.role === 'Coordinador');
        } else if (
            formikFormData.values.assignmentType === 'Pruebas de Laboratorio' ||
            formikFormData.values.assignmentType === 'Validación de muestras'
        ) {
            return users.filter(u => u.role === 'Laboratorista');
        } else if (formikFormData.values.assignmentType === 'Encuesta de satisfacción') {
            return users.filter(u => u.role === 'Laboratorista' || u.role === 'Coordinador' || u.role === 'Asesor Técnico');
        }
        return users;
    }, [users, formikFormData.values.assignmentType]);

    // Filter lab areas based on selected laboratory type
    const filteredLabAreas = React.useMemo(() => {
        if (!formikFormData.values.laboratoryType) return [];
        return labAreas.filter(area => area.laboratoryType?.id === formikFormData.values.laboratoryType);
    }, [labAreas, formikFormData.values.laboratoryType]);

    const children_form = (
        <div>
            <h4 className="text-primary m-0 ps-4">Registro/Actualización de Regla de Asignación</h4>
            <GradientDecorationLine />

            <Form onSubmit={formikFormData.handleSubmit} className="form-horizontal mt-3 mb-1">
                {/* Tipo de Proceso */}
                <div className="mb-3 px-5">
                    <Label className='text-primary'>Tipo de Proceso</Label>
                    <BaseSelectComponent
                        options={[
                            { value: 'Validación de informes', label: 'Validación de informes' },
                            { value: 'Pruebas de Laboratorio', label: 'Pruebas de Laboratorio' },
                            { value: 'Validación de muestras', label: 'Validación de muestras' },
                            { value: 'Encuesta de satisfacción', label: 'Encuesta de satisfacción' }
                        ]}
                        value={formikFormData.values.assignmentType}
                        onChange={(value) => {
                            formikFormData.setFieldValue('assignmentType', value);
                            if (value !== 'Pruebas de Laboratorio') {
                                formikFormData.setFieldValue('laboratoryArea', '');
                            }
                        }}
                        placeholder="Seleccione el proceso"
                        error="Seleccione el tipo de proceso"
                        touched={formikFormData.touched.assignmentType && formikFormData.errors.assignmentType}
                        status={formikFormData.touched.assignmentType && formikFormData.errors.assignmentType ? 'error' : ''}
                    />
                </div>

                {/* Tipo de Laboratorio */}
                <div className="mb-3 px-5">
                    <Label className='text-primary'>Tipo de Laboratorio</Label>
                    <BaseSelectComponent
                        options={labTypes.map(item => ({ value: item.id, label: item.name }))}
                        value={formikFormData.values.laboratoryType}
                        onChange={(value) => {
                            formikFormData.setFieldValue('laboratoryType', value);
                            formikFormData.setFieldValue('laboratoryArea', '');
                        }}
                        placeholder="Seleccione el tipo de laboratorio"
                        error="Seleccione el tipo de laboratorio"
                        touched={formikFormData.touched.laboratoryType && formikFormData.errors.laboratoryType}
                        status={formikFormData.touched.laboratoryType && formikFormData.errors.laboratoryType ? 'error' : ''}
                    />
                </div>

                {/* Área de Laboratorio (condicional) */}
                {formikFormData.values.assignmentType === 'Pruebas de Laboratorio' && (
                    <div className="mb-3 px-5">
                        <Label className='text-primary'>Área de Laboratorio</Label>
                        <BaseSelectComponent
                            options={filteredLabAreas.map(item => ({ value: item.id, label: item.name }))}
                            value={formikFormData.values.laboratoryArea}
                            onChange={(value) => formikFormData.setFieldValue('laboratoryArea', value)}
                            placeholder="Seleccione el área de laboratorio"
                            error="Seleccione el área de laboratorio"
                            touched={formikFormData.touched.laboratoryArea && formikFormData.errors.laboratoryArea}
                            status={formikFormData.touched.laboratoryArea && formikFormData.errors.laboratoryArea ? 'error' : ''}
                        />
                    </div>
                )}

                {/* Usuario Responsable */}
                <div className="mb-3 px-5">
                    <Label className='text-primary'>Usuario Responsable</Label>
                    <BaseSelectComponent
                        options={filteredUsers.map(item => ({
                            value: item.id,
                            label: `${item.name ?? ''} ${item.surname ?? ''}`.trim() + ` (${item.role})`
                        }))}
                        value={formikFormData.values.user}
                        onChange={(value) => formikFormData.setFieldValue('user', value)}
                        placeholder="Seleccione el usuario"
                        error="Seleccione el usuario responsable"
                        touched={formikFormData.touched.user && formikFormData.errors.user}
                        status={formikFormData.touched.user && formikFormData.errors.user ? 'error' : ''}
                    />
                </div>

                {/* Estatus Activo */}
                <div className="px-5">
                    <div className='text-primary pb-2'>Activo</div>
                    <Switch
                        disabled={lockActive}
                        className='me-4'
                        uncheckedIcon={<Offsymbol />}
                        checkedIcon={<OnSymbol />}
                        onColor="#94C12E"
                        onChange={() => setActive(!active)}
                        checked={active}
                    />
                </div>

                <div className='mt-5'>
                    <GradientDecorationLine />
                    <div className="page-title-box d-flex justify-content-end">
                        <BaseCustomButton
                            outline
                            color="primary"
                            className='rounded-3 me-3'
                            onClick={formikFormData.handleSubmit}
                            icon='mdi mdi-content-save me-1'
                            label='Guardar'
                        />
                        <BaseCustomButton
                            outline
                            color="danger"
                            className='rounded-3'
                            onClick={onClosed}
                            icon='mdi mdi-close-circle-outline me-1'
                            label='Cancelar'
                        />
                    </div>
                </div>
            </Form>
        </div>
    );

    return (
        <BaseCustomModal
            isModalOpen={showModal}
            handleOk={onClosed}
            handleCancel={onClosed}
            footer={null}
            children={children_form}
        />
    );
};
